import { gql } from '@apollo/client'
import {
  BookmarkActiveIcon,
  BookmarkIcon,
  CheckRoundIcon,
  Confirm,
  DeleteIcon,
  Dropdown,
  EditIcon,
  FileSearchIcon,
  FlagIcon,
  IconButton,
  Link,
  MenuItem,
  MoreVerticalIcon,
  PrintIcon,
  Spinner,
  useToast,
} from '@farol-ds/react'
import { useAppBarContext } from '@jusbrasil-web/shared-appbar'
import { LoginTrigger } from '@jusbrasil-web/shared-bifrost'
import { COMPONENT_CLASS } from '@jusbrasil-web/shared-ga4-events'
import { useOnScreen } from '@jusbrasil-web/shared-hooks'
import { composeRefs } from '@radix-ui/react-compose-refs'
import classNames from 'classnames'
import { forwardRef, ReactNode, RefObject, useState } from 'react'

import {
  useDeleteDocument,
  useDocumentModerationModal,
  useDocumentReportModal,
  useMetrics,
  useSaveDocument,
  useToolbar,
} from '../../hooks'
import printStyles from '../../print.module.scss'
import toolbarStyles from '../../toolbar.module.scss'
import { DocSave } from '../doc-save'
import { DocSearchElement } from '../doc-search'
import { PartialGA4FeatureUseEventProps } from '../ga4-utils'
import {
  DocToolbar_DocumentFragment,
  DocToolbar_ViewerFragment,
} from './__generated__/doc-toolbar.graphql'

export type DocToolbarElement = HTMLDivElement

export interface DocToolbarProps {
  document: DocToolbar_DocumentFragment
  viewer: DocToolbar_ViewerFragment | null
  docSearchRef: RefObject<DocSearchElement>
  className?: string
  mainActions?: ReactNode
  ga4SearchEventData?: PartialGA4FeatureUseEventProps
  ga4DocumentSaveEventData?: PartialGA4FeatureUseEventProps
  isPageEmpty?: boolean
  ctaFullWidth?: boolean
}

const DocToolbarRoot = forwardRef<DocToolbarElement, DocToolbarProps>(function DocToolbar(
  props,
  forwardedRef
) {
  const {
    document,
    viewer,
    docSearchRef,
    className,
    mainActions,
    ga4SearchEventData,
    ga4DocumentSaveEventData,
    isPageEmpty,
    ctaFullWidth,
  } = props
  const { docId, artifact, viewerSaved } = document
  const { componentClass: rawComponentClass, featureName } = ga4SearchEventData || {}
  const componentClass = rawComponentClass ?? COMPONENT_CLASS.DOCUMENT_HEADER
  const documentReport = useDocumentReportModal()
  const documentModeration = useDocumentModerationModal()
  const { ref: dispatcherRef, sendMetrics } = useMetrics<HTMLDivElement>()
  const toast = useToast()
  const deleteDocument = useDeleteDocument({
    artifact,
    document,
    docId,
  })
  const { toggleSave } = useSaveDocument({
    document,
    viewer,
    ga4EventData: ga4DocumentSaveEventData,
    sendMetrics,
  })

  const [isDeleteConfirmOpened, setDeleteConfirmOpened] = useState(false)
  const { onSearchButtonClick, onPrint, onReport, onDelete, onModeration } = useToolbar({
    document,
    docSearchRef,
    toast,
    deleteDocument,
    setDeleteConfirmOpened,
    documentModeration,
    documentReport,
    sendMetrics,
    featureName,
    componentClass,
  })

  const { stickyTriggerRef: toolBarRefElement } = useAppBarContext()

  const isAuthor = !!document.publisher?.isViewer
  const isAdmin = !!viewer?.user?.isAdmin
  const hasPaidPlan = !!viewer?.hasPaidPlan

  const toolbarSticky = !useOnScreen(toolBarRefElement, { threshold: 0 }, true)

  return (
    <div
      className={classNames(toolbarStyles.toolbar, className, printStyles.unprintable)}
      ref={composeRefs(forwardedRef, dispatcherRef)}
    >
      {mainActions}

      <div
        className={classNames([
          toolbarStyles.toolbarIconBtns,
          { [toolbarStyles.hideIconsWhenMobile]: ctaFullWidth && toolbarSticky },
        ])}
      >
        {hasPaidPlan && !isPageEmpty && (
          <IconButton
            icon={<FileSearchIcon />}
            onClick={onSearchButtonClick}
            data-testid="search-btn"
            label="Buscar no documento"
          />
        )}

        <div className={toolbarStyles.toolbarSaveButton}>
          <DocSave viewer={viewer} document={document} ga4EventData={ga4DocumentSaveEventData} />
        </div>

        <Dropdown
          onOpenChange={(open) => {
            if (open) {
              documentReport.init()
              documentModeration.init()
            }
          }}
        >
          <Dropdown.Trigger>
            <IconButton icon={<MoreVerticalIcon />} label="Mais" />
          </Dropdown.Trigger>

          <Dropdown.Menu
            title="Mais opções"
            /* no autofocus after interaction with the dropdown (then close), better UX since the items open modals */
            onCloseAutoFocus={(evt) => evt.preventDefault()}
          >
            <LoginTrigger isLogged={!!viewer}>
              <Dropdown.MenuItem onClick={toggleSave} className={toolbarStyles.saveMenuItem}>
                <MenuItem>
                  <MenuItem.Content>
                    <MenuItem.Leading>
                      {viewerSaved ? <BookmarkActiveIcon /> : <BookmarkIcon />}
                    </MenuItem.Leading>
                    Salvar
                  </MenuItem.Content>
                </MenuItem>
              </Dropdown.MenuItem>
            </LoginTrigger>
            {(isAdmin || isAuthor) && (
              <>
                <Dropdown.MenuItem>
                  <MenuItem>
                    <Link
                      type="ghost"
                      href={document.editUrl || ''}
                      className={toolbarStyles.toolbarDocumentEditUrl}
                      data-testid="toolbar-document-edit-url"
                    >
                      <MenuItem.Content>
                        <MenuItem.Leading>
                          <EditIcon />
                        </MenuItem.Leading>
                        Editar
                      </MenuItem.Content>
                    </Link>
                  </MenuItem>
                </Dropdown.MenuItem>
                {isAdmin && (
                  <Dropdown.MenuItem onClick={onModeration} disabled={!documentModeration.ready}>
                    <MenuItem>
                      <MenuItem.Content>
                        <MenuItem.Leading>
                          {documentModeration.loading ? <Spinner size="xs" /> : <CheckRoundIcon />}
                        </MenuItem.Leading>
                        Moderar
                      </MenuItem.Content>
                    </MenuItem>
                  </Dropdown.MenuItem>
                )}
              </>
            )}
            <Dropdown.MenuItem onClick={onPrint}>
              <MenuItem>
                <MenuItem.Content>
                  <MenuItem.Leading>
                    <PrintIcon />
                  </MenuItem.Leading>
                  Imprimir
                </MenuItem.Content>
              </MenuItem>
            </Dropdown.MenuItem>
            <Dropdown.MenuItem onClick={onReport} disabled={!documentReport.ready}>
              <MenuItem>
                <MenuItem.Content>
                  <MenuItem.Leading>
                    {documentReport.loading ? <Spinner size="xs" /> : <FlagIcon />}
                  </MenuItem.Leading>
                  Reportar
                </MenuItem.Content>
              </MenuItem>
            </Dropdown.MenuItem>
            {(isAdmin || isAuthor) && (
              <Dropdown.MenuItem onClick={() => setDeleteConfirmOpened(true)}>
                <MenuItem type="danger">
                  <MenuItem.Content>
                    <MenuItem.Leading>
                      <DeleteIcon />
                    </MenuItem.Leading>
                    Deletar
                  </MenuItem.Content>
                </MenuItem>
              </Dropdown.MenuItem>
            )}
          </Dropdown.Menu>
        </Dropdown>

        {(isAdmin || isAuthor) && (
          <Confirm
            title="Deseja realmente deletar este documento?"
            confirmLabel="OK"
            confirmProps={{
              loading: deleteDocument.loading,
            }}
            danger
            open={isDeleteConfirmOpened}
            onConfirm={onDelete}
            onCancel={() => setDeleteConfirmOpened(false)}
          />
        )}
      </div>
    </div>
  )
})

export const DocToolbar = Object.assign(DocToolbarRoot, {
  fragments: {
    document: gql`
      fragment DocToolbar_document on Document {
        docId
        artifact
        publisher {
          isViewer
        }
        editUrl
        viewerSaved

        ...DocSave_document
        ...UseSaveDocument_document
      }

      ${DocSave.fragments.document}
      ${useSaveDocument.fragments.document}
    `,

    viewer: gql`
      fragment DocToolbar_viewer on Profile {
        hasPaidPlan
        user {
          isAdmin
        }
        ...DocSave_viewer
        ...UseSaveDocument_viewer
      }

      ${DocSave.fragments.viewer}
      ${useSaveDocument.fragments.viewer}
    `,
  },
})
